import React, { useState } from "react";
import { Box, CircularProgress, Stack, Tooltip, Typography } from "@mui/material";
import { Icon } from "@iconify/react";
import Button from "../../components/buttons/Button";
import type { RejectedItem } from "../types";
import { card as cardCopy, interpolate } from "../copy";
import { cutOffText, formatSize } from "../../utils";

interface UploadErrorCardProps {
  item: RejectedItem;
  onRetry: () => Promise<void>;
}

interface ZipInvalidFile {
  filename: string;
  reason: string;
}

interface ZipDetails {
  invalid_filenames?: ZipInvalidFile[] | string[];
  allowed?: string[] | string;
}

const UploadErrorCard: React.FC<UploadErrorCardProps> = ({ item, onRetry }) => {
  const [retrying, setRetrying] = useState(false);
  const isRetry = item.error_effect === "RETRY";
  const isFatal = item.error_effect === "FATAL";
  const vars = { filename: item.filename };
  const bg = isFatal ? "#FFF7F5" : "#FFF8EE";
  const borderColor = isFatal ? "#D32F2F" : "#EF6C00";

  const handleRetry = async () => {
    setRetrying(true);
    try {
      await onRetry();
    } finally {
      setRetrying(false);
    }
  };

  const renderDetails = () => {
    if (!item.details) return null;

    if (item.error_code === "ZIP_CONTAINS_INVALID_FILES") {
      const details = item.details as ZipDetails;
      const invalidFiles = Array.isArray(details.invalid_filenames) ? details.invalid_filenames : [];
      const allowedRaw = details.allowed;
      const allowed: string[] = Array.isArray(allowedRaw)
        ? allowedRaw
        : typeof allowedRaw === "string"
        ? allowedRaw.split(/,\s*/)
        : [];
      return (
        <Box sx={{ mt: 1.5 }}>
          <Stack direction="row" spacing={3} alignItems="flex-start">
            <Box>
              <Typography
                variant="caption"
                sx={{ color: "#D32F2F", fontWeight: 600, mb: 0.5, display: "block" }}
              >
                {typeof invalidFiles[0] === "string"
                  ? `Unsupported (${invalidFiles.length})`
                  : `Unsupported (${invalidFiles.length})`}
              </Typography>
              {invalidFiles.slice(0, 6).map((f, i) => {
                const name = typeof f === "string" ? f : (f as ZipInvalidFile).filename;
                return (
                  <Stack key={i} direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.25 }}>
                    <Icon icon="mdi:close" color="#D32F2F" width={12} />
                    <Tooltip title={name} placement="top">
                      <Typography variant="caption" sx={{ color: "#5B6B61", fontFamily: "'Roboto Mono', monospace" }}>
                        {cutOffText(name, 38, "middle")}
                      </Typography>
                    </Tooltip>
                  </Stack>
                );
              })}
            </Box>
            {allowed.length > 0 && (
              <Box>
                <Typography variant="caption" sx={{ color: "#5B6B61", display: "block" }}>
                  {cardCopy.allowedExtensions(allowed.join(", "))}
                </Typography>
              </Box>
            )}
          </Stack>
        </Box>
      );
    }

    if (item.error_code === "FILE_TOO_LARGE" || item.error_code === "UPLOAD_SIZE_EXCEEDED") {
      const details = item.details as { file_size?: number; max_size?: number };
      if (details.file_size && details.max_size) {
        return (
          <Typography variant="caption" sx={{ mt: 1, display: "block", color: "#5B6B61" }}>
            <Typography component="span" sx={{ color: "#D32F2F", fontWeight: 600 }}>
              {formatSize(details.file_size)}
            </Typography>
            {" · limit "}
            {formatSize(details.max_size)}
          </Typography>
        );
      }
    }

    if (item.error_code === "SIZE_MISMATCH") {
      const details = item.details as { declared_size?: number; received_size?: number };
      if (details.declared_size && details.received_size) {
        return (
          <Typography variant="caption" sx={{ mt: 1, display: "block", color: "#5B6B61" }}>
            Declared {formatSize(details.declared_size)} · received {formatSize(details.received_size)}.
          </Typography>
        );
      }
    }

    return null;
  };

  return (
    <Box
      sx={{
        borderLeft: `3px solid ${borderColor}`,
        borderRadius: "0 8px 8px 0",
        background: bg,
        padding: "14px 18px 16px 18px",
        mt: "4px",
      }}
    >
      <Stack spacing={0.75}>
        {item.error_message && (
          <Typography variant="body2" sx={{ fontWeight: 500, color: "#0B1F15" }}>
            {interpolate(item.error_message, vars)}
          </Typography>
        )}

        {item.action_item && (
          <Typography variant="body2" sx={{ fontWeight: 400, color: "#3F4F45" }}>
            <Typography component="span" sx={{ color: borderColor, mr: 0.5 }}>
              {cardCopy.whatToDoPrefix}
            </Typography>
            {interpolate(item.action_item, vars)}
          </Typography>
        )}

        {renderDetails()}

        <Stack
          direction="row"
          justifyContent="flex-end"
          alignItems="center"
          spacing={1}
          sx={{ mt: 1 }}
        >
          <Box
            component="span"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              height: "22px",
              padding: "0 8px",
              borderRadius: "6px",
              background: "rgba(0,0,0,0.05)",
              color: "#5B6B61",
              fontFamily: "'Roboto Mono', 'ui-monospace', monospace",
              fontSize: "11px",
            }}
          >
            {item.error_code}
          </Box>

          {isRetry && (
            <Button
              onClick={handleRetry}
              disabled={retrying}
              variant="contained"
              size="small"
              width="auto"
              sx={{
                height: "36px",
                padding: "0 16px",
                borderRadius: "24px",
                background: "#EF6C00",
                color: "white",
                fontWeight: 500,
                fontSize: "13px",
                "&:hover": {
                  background: "#E65100",
                  transform: "translateY(-1px)",
                  boxShadow: "0 4px 12px -4px rgba(239,108,0,0.5)",
                },
                "&:disabled": {
                  background: "rgba(239,108,0,0.5)",
                  color: "white",
                },
              }}
            >
              {retrying ? (
                <CircularProgress size={16} sx={{ color: "white", mr: 0.75 }} />
              ) : (
                <Icon icon="mdi:reload" width={16} style={{ marginRight: 6 }} />
              )}
              {cardCopy.retry}
            </Button>
          )}
        </Stack>
      </Stack>
    </Box>
  );
};

export default UploadErrorCard;
